import { parseISO } from "date-fns";
import { Check, ChevronLeft, Copy, Pencil, Power, PowerOff } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { Badge, Button, Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui";
import type { GroupDetailUI } from "~/features/admin/groups/types";
import { GroupActivationDialog } from "./GroupActivationDialog";

interface GroupDetailPresenterProps {
  group: GroupDetailUI;
  onBack: () => void;
  onEdit: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  pendingAction: "activate" | "deactivate" | null;
  processing: boolean;
  onConfirm: () => void;
  onCancelConfirm: () => void;
  /** Optional "users in group" section, rendered below the properties (Peek only — V1 parity). */
  usersSection?: React.ReactNode;
}

export function GroupDetailPresenter({
  group,
  onBack,
  onEdit,
  onActivate,
  onDeactivate,
  pendingAction,
  processing,
  onConfirm,
  onCancelConfirm,
  usersSection,
}: GroupDetailPresenterProps) {
  const { t } = useTranslation();

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: t("ui.name"), value: group.name || "—" },
    { label: t("ui.description"), value: group.description || "—" },
    { label: t("ui.status"), value: group.isDeactivated ? t("ui.inactive") : t("ui.active") },
  ];

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <Button type="button" variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ChevronLeft />
        <span>{t("ui.backToList")}</span>
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-dewey-title">{group.name}</h1>
          {group.isDeactivated && <Badge tone="default">{t("ui.deactivated")}</Badge>}
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil />
            <span>{t("ui.edit")}</span>
          </Button>
          {group.isDeactivated ? (
            <Button type="button" variant="ghost" size="sm" onClick={onActivate} disabled={processing}>
              <Power />
              <span>{t("ui.activate")}</span>
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={onDeactivate} disabled={processing}>
              <PowerOff />
              <span>{t("ui.deactivate")}</span>
            </Button>
          )}
        </div>
      </div>

      <SubHeader group={group} />

      <section className="space-y-2">
        <h2 className="text-16-bold leading-tight capitalize">{t("ui.name")}</h2>
        <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
          <table className="w-full border-collapse">
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.label} className={i < rows.length - 1 ? "border-b border-border-default" : undefined}>
                  <th className="w-44 bg-bg-subtle px-3.5 py-2 text-left text-12-medium text-fg-2 align-top">
                    {row.label}
                  </th>
                  <td className="px-3.5 py-2 text-14-regular text-fg-1">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {usersSection && (
        <section className="space-y-2">
          <h2 className="text-16-bold leading-tight capitalize">{t("model.user.modelNames")}</h2>
          {usersSection}
        </section>
      )}

      <GroupActivationDialog
        open={pendingAction !== null}
        action={pendingAction ?? "deactivate"}
        name={group.name}
        processing={processing}
        onConfirm={onConfirm}
        onCancel={onCancelConfirm}
      />
    </div>
  );
}

function SubHeader({ group }: { group: GroupDetailUI }) {
  const { t } = useTranslation();
  const items: { label: string; value: React.ReactNode }[] = [
    { label: t("model.common.id"), value: <CopyableText text={group.id} className="font-mono" /> },
  ];
  if (group.createdAt) {
    items.push({ label: t("model.common.createdAt"), value: t("date.long", { date: parseISO(group.createdAt) }) });
  }
  if (group.updatedAt && group.updatedAt !== group.createdAt) {
    items.push({ label: t("model.common.updatedAt"), value: t("date.long", { date: parseISO(group.updatedAt) }) });
  }
  if (group.deactivatedAt) {
    items.push({
      label: t("model.common.deactivatedAt"),
      value: t("date.long", { date: parseISO(group.deactivatedAt) }),
    });
  }
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-12-regular text-fg-3">
      {items.map((item, i) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          {i > 0 && (
            <span aria-hidden className="text-border-strong">
              |
            </span>
          )}
          <span className="font-medium uppercase tracking-[0.04em]">{item.label}</span>
          <span className="text-fg-2 tabular-nums">{item.value}</span>
        </span>
      ))}
    </div>
  );
}

function CopyableText({ text, className }: { text: string; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await copyToClipboard(text);
      setCopied(true);
      toast.success(t("feature.common.idCopied"));
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={className}>{text}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex h-5 w-5 items-center justify-center rounded-[var(--radius-xs)] text-fg-3 hover:bg-bg-subtle hover:text-fg-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
            aria-label={t("ui.copyId")}
          >
            {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
          </button>
        </TooltipTrigger>
        <TooltipContent>{t("ui.copyId")}</TooltipContent>
      </Tooltip>
    </span>
  );
}
